import { AxiosInstance } from "axios"
import {
    BookingSummaryReport,
    CancellationRateReport,
    CustomerUsageRateReport,
    GuestsSummaryReport,
    OccupancyRateReport,
    PeakHoursSummaryReport,
} from "./statisticsTypes"
import { ResourceType } from "../../admin/organization/resources/resourcesTypes"

export enum ServiceCenterStatisticsQuery {
    SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE_PER_RESOURCE = "SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE_PER_RESOURCE",
    SERVICE_CENTER_STATISTICS_CANCELLATION_RATE_PER_RESOURCE = "SERVICE_CENTER_STATISTICS_CANCELLATION_RATE_PER_RESOURCE",
    SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY_PER_RESOURCE = "SERVICE_CENTER_STATISTICS_BOOKINGS_SUMMARY_PER_RESOURCE",
    SERVICE_CENTER_STATISTICS_GUESTS_SUMMARY = "SERVICE_CENTER_STATISTICS_GUESTS_SUMMARY",
    SERVICE_CENTER_STATISTICS_PEAK_HOURS_SUMMARY = "SERVICE_CENTER_STATISTICS_PEAK_HOURS_SUMMARY",
    SERVICE_CENTER_STATISTICS_CUSTOMER_USAGE_SUMMARY = "SERVICE_CENTER_STATISTICS_CUSTOMER_USAGE_SUMMARY",
}

type ServiceCenterOccupancyRateReportParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
    resourceType: ResourceType
}>

export type ServiceCenterOccupancyRateReportResponse = OccupancyRateReport

export async function fetchOccupancyRatePerResource(
    params: ServiceCenterOccupancyRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterOccupancyRateReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/occupancyRatePerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterCancellationRateReportResponse = CancellationRateReport
type ServiceCenterCancellationRateReportParams = ServiceCenterOccupancyRateReportParams

export async function fetchCancellationRatePerResource(
    params: ServiceCenterCancellationRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterCancellationRateReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/cancellationRatePerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterBookingSummaryReportResponse = BookingSummaryReport
type ServiceCenterBookingSummaryRateReportParams = ServiceCenterOccupancyRateReportParams

export async function fetchBookingSummaryRatePerResource(
    params: ServiceCenterBookingSummaryRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterBookingSummaryReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/bookingsSummaryPerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

type ServiceCenterGuestsReportParams = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
}>

export type ServiceCenterGuestsSummaryReportResponse = GuestsSummaryReport
type ServiceCenterGuestsSummaryRateReportParams = ServiceCenterGuestsReportParams

export async function fetchGuestsSummaryRatePerResource(
    params: ServiceCenterGuestsSummaryRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterGuestsSummaryReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/guestsSummary`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterPeakHoursSummaryReportResponse = PeakHoursSummaryReport
type ServiceCenterPeakHoursSummaryRateReportParams = ServiceCenterOccupancyRateReportParams

export async function fetchPeakHoursSummaryRatePerResource(
    params: ServiceCenterPeakHoursSummaryRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterPeakHoursSummaryReportResponse> {
    const { serviceCenterId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/dashboard/hourlyOccupancyRatePerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}

export type ServiceCenterCustomerUsageRateReportResponse = CustomerUsageRateReport

type ServiceCenterCustomerUsageRateReportParams = ServiceCenterOccupancyRateReportParams & {
    companyId?: number
}

export async function fetchCustomerUsageRatePerResource(
    params: ServiceCenterCustomerUsageRateReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<ServiceCenterCustomerUsageRateReportResponse> {
    const { serviceCenterId, companyId, ...rest } = params
    const url = `/serviceCenters/${serviceCenterId}/companies/${companyId}/dashboard/occupancyRatePerResource`

    const { data } = await axios.get(url, {
        signal,
        params: {
            ...rest,
        },
    })

    return data
}
